import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Edit,
  PlusCircle,
  Search,
  Trash2,
  Server,
  GitBranch,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Cloud,
  FileEdit,
  Shield,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import { useDebounce } from '@/utils/debounce';
import tenantService from '@/services/tenantService';
import { TenantResponseDto, TenantStatus, GitLabIngressHistory, SuspensionConfig } from '@/types/tenant.types';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { Select, SelectOption } from '@/components/ui/Select';
import InputField from '@/components/common/form-fields/InputField';
import IngressYAMLModal from '@/components/tenants/IngressYAMLModal';
import IngressHistoryModal from '@/components/tenants/IngressHistoryModal';
import IngressYAMLEditorModal from '@/components/tenants/IngressYAMLEditorModal';
import CloudFlareDNSManagerModal from '@/components/tenants/CloudFlareDNSManagerModal';
import BlockTenantModal from '@/components/tenants/BlockTenantModal';
import ConfirmationModal from '@/components/ui/ConfirmationModal';

const TenantStatusBadge = ({ status }: { status?: TenantStatus }) => {
  const getStatusConfig = (status?: TenantStatus) => {
    switch (status) {
      case TenantStatus.ACTIVE:
        return {
          className: 'bg-green-100 text-green-700 border-green-300',
          icon: '🟢',
          label: 'Active'
        };
      case TenantStatus.INACTIVE:
        return {
          className: 'bg-gray-100 text-gray-700 border-gray-300',
          icon: '⚫',
          label: 'Inactive'
        };
      case TenantStatus.SUSPENDED:
        return {
          className: 'bg-red-100 text-red-700 border-red-300',
          icon: '🔴',
          label: 'Suspended'
        };
      default:
        return {
          className: 'bg-gray-100 text-gray-700 border-gray-300',
          icon: '❓',
          label: 'Unknown'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.className}`}>
      {config.icon} {config.label}
    </span>
  );
};

const GitLabIngressBadge = ({
  status,
  commitId,
  error,
  onClick
}: {
  status?: 'pending' | 'success' | 'failed';
  commitId?: string;
  error?: string;
  onClick?: () => void;
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
          icon: <CheckCircle className="w-3 h-3" />,
          label: 'Synced',
          title: commitId ? `Commit: ${commitId.substring(0, 8)} (Click for history)` : 'Successfully synced (Click for history)'
        };
      case 'pending':
        return {
          className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
          icon: <Clock className="w-3 h-3" />,
          label: 'Pending',
          title: 'GitLab sync in progress (Click for history)'
        };
      case 'failed':
        return {
          className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
          icon: <XCircle className="w-3 h-3" />,
          label: 'Failed',
          title: (error || 'GitLab sync failed') + ' (Click for history)'
        };
      default:
        return {
          className: 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100',
          icon: <GitBranch className="w-3 h-3" />,
          label: 'N/A',
          title: 'Not synced yet (Click for history)'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border transition-colors cursor-pointer ${config.className}`}
      title={config.title}
    >
      {config.icon}
      {config.label}
    </button>
  );
};

const CloudFlareDNSBadge = ({
  status,
  recordCount,
  error,
  onClick
}: {
  status?: 'pending' | 'success' | 'failed' | 'partial';
  recordCount?: number;
  error?: string;
  onClick?: () => void;
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          className: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
          icon: <Cloud className="w-3 h-3" />,
          label: `${recordCount || 0} DNS`,
          title: `${recordCount || 0} CNAME records created (Click to view)`
        };
      case 'partial':
        return {
          className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
          icon: <Cloud className="w-3 h-3" />,
          label: 'Partial',
          title: `Some DNS records failed: ${error || 'Partial success'} (Click to view)`
        };
      case 'pending':
        return {
          className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
          icon: <Clock className="w-3 h-3" />,
          label: 'Pending',
          title: 'DNS creation in progress (Click to view)'
        };
      case 'failed':
        return {
          className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
          icon: <XCircle className="w-3 h-3" />,
          label: 'Failed',
          title: `${error || 'DNS creation failed'} (Click to view)`
        };
      default:
        return {
          className: 'bg-gray-50 text-gray-500 border-gray-200',
          icon: <Cloud className="w-3 h-3" />,
          label: 'N/A',
          title: 'No DNS records'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border transition-colors ${config.className} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
      title={config.title}
    >
      {config.icon}
      {config.label}
    </button>
  );
};

interface FilterForm {
  search: string;
  status: string;
  sort: string; // Combined sortBy-sortOrder
}

export default function TenantsList() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantResponseDto | null>(null);

  // Block Tenant Modal
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [tenantToBlock, setTenantToBlock] = useState<TenantResponseDto | null>(null);

  // Unblock Tenant Confirmation Modal
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [tenantToUnblock, setTenantToUnblock] = useState<TenantResponseDto | null>(null);
  const [unblockLoading, setUnblockLoading] = useState(false);

  // GitLab Ingress Modals
  const [yamlModalOpen, setYamlModalOpen] = useState(false);
  const [yamlEditorModalOpen, setYamlEditorModalOpen] = useState(false);
  const [yamlContent, setYamlContent] = useState('');
  const [yamlLoading, setYamlLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedTenantForHistory, setSelectedTenantForHistory] = useState<TenantResponseDto | null>(null);
  const [ingressHistory, setIngressHistory] = useState<GitLabIngressHistory[]>([]);

  // CloudFlare DNS Modals
  const [dnsModalOpen, setDnsModalOpen] = useState(false);
  const [dnsRecords, setDnsRecords] = useState<any[]>([]);
  const [dnsLoading, setDnsLoading] = useState(false);

  const { control, register, watch } = useForm<FilterForm>({
    defaultValues: {
      search: '',
      status: '',
      sort: 'updatedAt-desc',
    }
  });

  const watchedFilters = watch();

  // Debounce search input
  const debouncedSearch = useDebounce(watchedFilters.search, 1000);

  const queryParams = useMemo(() => {
    // Split sort into sortBy and sortOrder
    const [sortBy, sortOrder] = watchedFilters.sort.split('-');

    const params: any = {
      page,
      limit: itemsPerPage,
      search: debouncedSearch,
      sortBy,
      sortOrder,
    };

    if (watchedFilters.status) {
      params.status = watchedFilters.status;
    }

    // Remove empty values
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    return params;
  }, [watchedFilters, debouncedSearch, page, itemsPerPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', queryParams],
    queryFn: () => tenantService.getAllTenants(queryParams),
  });

  // Fetch tenant metrics (not affected by filters)
  const { data: metrics } = useQuery({
    queryKey: ['tenant-metrics'],
    queryFn: () => tenantService.getTenantMetrics(),
  });

  // Fetch management tenant info (env MANAGEMENT_KEY_NAME → Tenants DB lookup)
  const { data: managementInfo } = useQuery({
    queryKey: ['tenant-management-info'],
    queryFn: () => tenantService.getManagementTenantInfo(),
  });

  if (error) toast.error('Failed to fetch tenants.');

  const tenants = data?.items || [];
  const totalTenants = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  // Get metrics from API
  const totalTenantsMetric = metrics?.total || 0;
  const activeTenants = metrics?.active || 0;
  const suspendedTenants = metrics?.suspended || 0;

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleDeleteClick = (tenant: TenantResponseDto) => {
    setTenantToDelete(tenant);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;

    try {
      await tenantService.deleteTenant(tenantToDelete.tenant_key);
      toast.success('Tenant deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setDeleteModalOpen(false);
      setTenantToDelete(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete tenant');
    }
  };

  const handleBlockClick = (tenant: TenantResponseDto) => {
    setTenantToBlock(tenant);
    setBlockModalOpen(true);
  };

  const handleBlockConfirm = async (suspensionConfig: SuspensionConfig) => {
    if (!tenantToBlock) return;

    try {
      // Update tenant with suspension config and change status to SUSPENDED
      await tenantService.updateTenant(tenantToBlock.tenant_key, {
        status: TenantStatus.SUSPENDED,
        suspension_config: suspensionConfig
      });

      toast.success('Tenant blocked successfully');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setBlockModalOpen(false);
      setTenantToBlock(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to block tenant');
    }
  };

  const handleUnblockClick = (tenant: TenantResponseDto) => {
    setTenantToUnblock(tenant);
    setUnblockModalOpen(true);
  };

  const handleUnblockConfirm = async () => {
    if (!tenantToUnblock) return;

    setUnblockLoading(true);
    try {
      await tenantService.updateTenant(tenantToUnblock.tenant_key, {
        status: TenantStatus.ACTIVE,
        suspension_config: null
      });

      toast.success('Tenant unblocked successfully');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setUnblockModalOpen(false);
      setTenantToUnblock(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to unblock tenant');
    } finally {
      setUnblockLoading(false);
    }
  };

  // Handle View YAML
  const handleViewYAML = async () => {
    setYamlLoading(true);
    setYamlModalOpen(true);
    try {
      const result = await tenantService.viewIngressYAML();
      setYamlContent(result.yaml);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch ingress YAML');
      setYamlModalOpen(false);
    } finally {
      setYamlLoading(false);
    }
  };

  // Handle Edit YAML
  const handleEditYAML = async () => {
    setYamlLoading(true);
    setYamlEditorModalOpen(true);
    try {
      const result = await tenantService.viewIngressYAML();
      setYamlContent(result.yaml);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch ingress YAML');
      setYamlEditorModalOpen(false);
    } finally {
      setYamlLoading(false);
    }
  };

  // Handle View CloudFlare DNS (All records)
  const handleViewDNS = async () => {
    setDnsLoading(true);
    setDnsModalOpen(true);
    try {
      const result = await tenantService.viewCloudFlareDNS();
      setDnsRecords(result.records);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch DNS records');
      setDnsModalOpen(false);
    } finally {
      setDnsLoading(false);
    }
  };

  // Handle View Tenant DNS (Specific tenant's records via badge click)
  const handleViewTenantDNS = async (tenant: TenantResponseDto) => {
    setDnsLoading(true);
    setDnsModalOpen(true);
    try {
      const result = await tenantService.getTenantDNSRecords(tenant.tenant_key);
      setDnsRecords(result.records);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch DNS records');
      setDnsModalOpen(false);
    } finally {
      setDnsLoading(false);
    }
  };

  // Handle Save Edited YAML
  const handleSaveYAML = async (yaml: string, commitMessage: string) => {
    await tenantService.editIngressYAML(yaml, commitMessage);
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    setYamlContent(yaml);
  };

  // Handle Manual Sync
  const handleSyncIngress = async (tenant: TenantResponseDto) => {
    try {
      toast.loading('Syncing tenant to ingress...', { id: 'sync-ingress' });
      const result = await tenantService.syncTenantToIngress(tenant.tenant_key);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(result.message, { id: 'sync-ingress' });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to sync ingress', { id: 'sync-ingress' });
    }
  };

  // Handle View History
  const handleViewHistory = async (tenant: TenantResponseDto) => {
    setSelectedTenantForHistory(tenant);
    setHistoryModalOpen(true);
    try {
      const result = await tenantService.getIngressHistory(tenant.tenant_key);
      setIngressHistory(result.history);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch ingress history');
      setIngressHistory([]);
    }
  };

  // Status options
  const statusOptions: SelectOption[] = [
    { label: 'All Status', value: '' },
    { label: 'Active', value: TenantStatus.ACTIVE },
    { label: 'Inactive', value: TenantStatus.INACTIVE },
    { label: 'Suspended', value: TenantStatus.SUSPENDED },
  ];

  // Sort options
  const sortOptions: SelectOption[] = [
    { label: 'Recently Updated', value: 'updatedAt-desc' },
    { label: 'Newest', value: 'createdAt-desc' },
    { label: 'Name (A-Z)', value: 'name-asc' },
    { label: 'Name (Z-A)', value: 'name-desc' },
    { label: 'Tenant Key (A-Z)', value: 'tenant_key-asc' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header Skeleton */}
        <div className="bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <SkeletonLoader type="custom" width="40px" height="40px" className="rounded-lg bg-white/20" />
              <div className="flex-1">
                <SkeletonLoader type="title" className="h-6 sm:h-8 w-40 sm:w-48 mb-2 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 sm:h-4 w-64 sm:w-96 bg-white/20 hidden sm:block" />
              </div>
            </div>
            <SkeletonLoader type="custom" width="180px" height="40px" className="rounded-lg bg-white/20" />
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <SkeletonLoader type="text" className="h-5 sm:h-7 w-12 sm:w-16 mb-1 bg-white/30" />
                <SkeletonLoader type="text" className="h-3 w-16 sm:w-20 bg-white/20" />
              </div>
            ))}
          </div>
        </div>

        {/* Search and Controls Skeleton */}
        <div className="card p-4 md:p-6">
          <div className="flex flex-col gap-4 mb-6">
            <SkeletonLoader type="custom" height="44px" className="rounded-lg w-full" />
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="card overflow-hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="p-4 border-b last:border-b-0">
              <SkeletonLoader type="text" className="mb-2" />
              <SkeletonLoader type="text" className="w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with Stats */}
      <div className="bg-gradient-to-r from-brand-blue-500 to-brand-indigo-500 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Tenants</h1>
              <p className="text-sm sm:text-base text-brand-blue-100 hidden sm:block">Manage multi-tenant configurations and settings</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleViewYAML}
              title="View YAML"
              className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap"
            >
              <GitBranch className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base hidden sm:inline">View YAML</span>
            </button>
            <button
              onClick={handleEditYAML}
              title="Edit YAML"
              className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap"
            >
              <FileEdit className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base hidden sm:inline">Edit YAML</span>
            </button>
            <button
              onClick={handleViewDNS}
              title="Manage DNS"
              className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap"
            >
              <Cloud className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base hidden sm:inline">Manage DNS</span>
            </button>
            <Link
              to="/admin/tenants/create"
              className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap"
            >
              <PlusCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Create<span className="hidden sm:inline"> Tenant</span></span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{totalTenantsMetric}</div>
            <div className="text-xs sm:text-sm text-brand-blue-200">Total</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{activeTenants}</div>
            <div className="text-xs sm:text-sm text-brand-blue-200">Active</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
            <div className="text-lg sm:text-2xl font-bold">{suspendedTenants}</div>
            <div className="text-xs sm:text-sm text-brand-blue-200">Suspended</div>
          </div>
        </div>
      </div>
      {/* Search and Filters */}
      <div className="card p-4 md:p-6">
        <div className="flex flex-col gap-4 mb-6">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
            <InputField
              fieldType="text"
              placeholder="Search tenants by name or key..."
              className="pl-10"
              {...register('search')}
            />
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Status Filter */}
            <div className="w-full sm:w-52">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <Select
                name="status"
                control={control}
                options={statusOptions}
                placeholder="All Status"
                isSearchable={false}
              />
            </div>

            {/* Sort Select */}
            <div className="w-full sm:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sort By
              </label>
              <Select
                name="sort"
                control={control}
                options={sortOptions}
                placeholder="Sort by"
                isSearchable={false}
              />
            </div>
          </div>
        </div>

        {/* Tenants Cards */}
        <div className="space-y-3">
          {tenants.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Server className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No tenants found</p>
            </div>
          ) : (
            tenants.map((tenant) => (
              <div
                key={tenant.id}
                className="card p-4 sm:p-5 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Tenant Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-brand-blue-500 to-brand-indigo-500 flex items-center justify-center">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title Row */}
                    <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 truncate">
                          {tenant.name}
                        </h3>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-600 break-all">
                          {tenant.tenant_key}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <TenantStatusBadge status={tenant.status} />
                      </div>
                    </div>

                    {/* Integration Status Row - Clickable badges with clear indicators */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <div className="flex items-center gap-2 group">
                        <span className="text-xs text-gray-500 font-medium">GitLab:</span>
                        <GitLabIngressBadge
                          status={tenant.integrations?.gitlab_ingress?.status}
                          commitId={tenant.integrations?.gitlab_ingress?.commit_id}
                          error={tenant.integrations?.gitlab_ingress?.error}
                          onClick={() => handleViewHistory(tenant)}
                        />
                        <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          Click to view history
                        </span>
                      </div>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-2 group">
                        <span className="text-xs text-gray-500 font-medium">DNS:</span>
                        <CloudFlareDNSBadge
                          status={tenant.integrations?.cloudflare_dns?.status}
                          recordCount={tenant.integrations?.cloudflare_dns?.records?.length}
                          error={tenant.integrations?.cloudflare_dns?.error}
                          onClick={() => handleViewTenantDNS(tenant)}
                        />
                        <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          Click to manage
                        </span>
                      </div>
                    </div>

                    {/* Domains Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      {tenant.domains.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 font-medium w-24">UI URL:</span>
                            <a
                              href={tenant.domains[0].admin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-blue-600 hover:underline truncate"
                            >
                              {tenant.domains[0].admin_url}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500 font-medium w-24">SERVICE URL:</span>
                            <a
                              href={tenant.domains[0].api_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-blue-600 hover:underline truncate"
                            >
                              {tenant.domains[0].api_url}
                            </a>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions Row — wraps on small screens; icon-only below sm */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                      <div className="flex flex-wrap items-center gap-2">
                        {(tenant.integrations?.gitlab_ingress?.status === 'failed' ||
                          tenant.integrations?.gitlab_ingress?.status === 'pending') && (
                          <button
                            onClick={() => handleSyncIngress(tenant)}
                            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors text-sm font-medium"
                            title="Retry GitLab Ingress Sync"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Sync Ingress</span>
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/admin/tenants/${tenant.tenant_key}/edit`}
                          title="Edit tenant"
                          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-brand-blue-50 hover:bg-brand-blue-100 text-brand-blue-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Edit</span>
                        </Link>
                        {tenant.status === TenantStatus.SUSPENDED ? (
                          <button
                            onClick={() => handleUnblockClick(tenant)}
                            title="Unblock tenant"
                            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm font-medium"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Unblock</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlockClick(tenant)}
                            title="Block tenant"
                            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors text-sm font-medium"
                          >
                            <Shield className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Block</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClick(tenant)}
                          title="Delete tenant"
                          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalTenants > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalItems={totalTenants}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTenantToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Tenant"
        message={
          <>
            Are you sure you want to delete tenant <strong>{tenantToDelete?.name}</strong>?
            <br />
            <span className="text-red-600 font-semibold">This action cannot be undone and will delete all associated data.</span>
          </>
        }
      />

      {/* Ingress YAML Viewer Modal */}
      <IngressYAMLModal
        isOpen={yamlModalOpen}
        onClose={() => setYamlModalOpen(false)}
        yamlContent={yamlContent}
        isLoading={yamlLoading}
        onRefresh={handleViewYAML}
      />

      {/* Ingress History Modal */}
      <IngressHistoryModal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedTenantForHistory(null);
          setIngressHistory([]);
        }}
        history={ingressHistory}
        tenantName={selectedTenantForHistory?.name || ''}
      />

      {/* Ingress YAML Editor Modal */}
      <IngressYAMLEditorModal
        isOpen={yamlEditorModalOpen}
        onClose={() => setYamlEditorModalOpen(false)}
        initialYAML={yamlContent}
        onSave={handleSaveYAML}
        isLoading={yamlLoading}
      />

      {/* CloudFlare DNS Manager Modal */}
      <CloudFlareDNSManagerModal
        isOpen={dnsModalOpen}
        onClose={() => setDnsModalOpen(false)}
        records={dnsRecords}
        isLoading={dnsLoading}
        onRefresh={handleViewDNS}
      />

      {/* Block Tenant Modal */}
      <BlockTenantModal
        isOpen={blockModalOpen}
        tenant={tenantToBlock}
        onClose={() => {
          setBlockModalOpen(false);
          setTenantToBlock(null);
        }}
        onConfirm={handleBlockConfirm}
      />

      {/* Unblock Tenant Confirmation Modal */}
      <ConfirmationModal
        isOpen={unblockModalOpen}
        title="Unblock Tenant"
        message={`Are you sure you want to unblock "${tenantToUnblock?.name}"? This will restore access for all users of this organization.`}
        type="success"
        confirmText={unblockLoading ? 'Unblocking...' : 'Unblock'}
        cancelText="Cancel"
        onConfirm={handleUnblockConfirm}
        onCancel={() => {
          setUnblockModalOpen(false);
          setTenantToUnblock(null);
        }}
      />
    </div>
  );
}
